import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SpotlightSearch from './SpotlightSearch';
import '../assets/styles/Project.scss';

type ManifestItem = { file: string; slug?: string; title: string; mtime?: string; type?: string; difficulty?: string; tags?: string[] };
type Writeup = { id: string; file?: string; title: string; date?: string; type?: string; difficulty?: string; tags?: string[] };

const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const INDEX_PATH = `${base}/writeups/index.json`;

const DIFFICULTY_COLORS: Record<string, string> = {
  easy:    'rgba(80,200,120,0.18)',
  medium:  'rgba(255,180,0,0.18)',
  hard:    'rgba(220,60,60,0.18)',
  high:    'rgba(220,60,60,0.18)',
  unknown: 'rgba(255,255,255,0.06)',
};

const DIFFICULTY_TEXT: Record<string, string> = {
  easy:    '#50c878',
  medium:  '#ffb400',
  hard:    '#e03c3c',
  high:    '#e03c3c',
  unknown: '#aaa',
};

/** Skill categories, matched against tags in order — first hit wins, so the more
 *  specific skill (forensics) claims a writeup before the broader one (cloud). */
const CATEGORIES: { id: string; label: string; tags: string[] }[] = [
  { id: 'forensics', label: 'Forensics & Incident Response', tags: ['dfir', 'forensics', 'cloudtrail', 'git'] },
  { id: 'cloud',     label: 'Cloud',                         tags: ['cloud', 'aws', 's3', 'ssm', 'sts', 'lambda', 'iam'] },
  { id: 'ai',        label: 'AI & LLM',                      tags: ['ai', 'llm', 'ml', 'prompt-injection', 'tokenizer'] },
  { id: 'web',       label: 'Web',                           tags: ['web', 'react', 'xss', 'ssrf', 'idor', 'sqli'] },
  { id: 'reversing', label: 'Reversing & Malware',           tags: ['rev', 'reversing', 'malware', 'binary', 'pwn'] },
  { id: 'crypto',    label: 'Crypto',                        tags: ['crypto', 'cryptography', 'hash'] },
  { id: 'network',   label: 'Network & Recon',               tags: ['dns', 'network', 'osint', 'recon'] },
];

const OTHER = { id: 'other', label: 'Other', tags: [] as string[] };

function categoryFor(w: Writeup) {
  const tags = (w.tags || []).map((t) => t.toLowerCase());
  return CATEGORIES.find((c) => c.tags.some((t) => tags.includes(t))) || OTHER;
}

export default function Writeups() {
  const [writeups, setWriteups] = useState<Writeup[]>([]);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ctf' | 'finding'>('finding');
  const [spotlightOpen, setSpotlightOpen] = useState(false);

  // Enter opens the search palette, Spotlight-style. Ignored while typing or
  // while a control has focus, so Enter still activates buttons and links.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Enter' || spotlightOpen) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || el?.isContentEditable) {
        return;
      }
      e.preventDefault();
      setSpotlightOpen(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [spotlightOpen]);

  useEffect(() => {
    let cancelled = false;

    async function loadManifest() {
      try {
        const res = await fetch(INDEX_PATH);
        if (!res.ok) throw new Error('no index');
        const list: ManifestItem[] = await res.json();
        const items = list.map((it) => ({
          id: it.slug || it.file.replace(/\.md$/i, '').replace(/\//g, '-'),
          file: it.file,
          title: it.title || it.file,
          date: it.mtime,
          type: it.type || 'ctf',
          difficulty: it.difficulty,
          tags: it.tags,
        } as Writeup));
        if (!cancelled) setWriteups(items);
      } catch (err) {
        console.warn('Could not load writeups manifest', err);
        if (!cancelled) setWriteups([]);
      }
    }

    loadManifest();
    return () => { cancelled = true; };
  }, []);

  const q = query.toLowerCase().trim();
  const filtered = writeups.filter((w) => {
    const matchesType = (w.type || 'ctf') === typeFilter;
    if (!matchesType) return false;
    if (!q) return true;
    return (
      w.title.toLowerCase().includes(q) ||
      (w.tags || []).some((t) => t.toLowerCase().includes(q)) ||
      (w.difficulty || '').toLowerCase().includes(q)
    );
  });

  // Group by skill, carrying a running offset so the card numbers read 1..n
  // straight down the page regardless of how many groups there are.
  let running = 0;
  const grouped = [...CATEGORIES, OTHER]
    .map((cat) => ({ cat, items: filtered.filter((w) => categoryFor(w).id === cat.id) }))
    .filter((g) => g.items.length > 0)
    .map(({ cat, items }) => {
      const offset = running;
      running += items.length;
      return { cat, items, offset };
    });

  // One card, shared by every group so the markup stays identical throughout.
  function renderCard(w: Writeup, idx: number) {
    const diff = (w.difficulty || 'unknown').toLowerCase();
    const type = (w.type || 'ctf').toLowerCase();
    const diffBg = DIFFICULTY_COLORS[diff] || DIFFICULTY_COLORS['unknown'];
    const diffColor = DIFFICULTY_TEXT[diff] || DIFFICULTY_TEXT['unknown'];
    return (
      <div className="project" key={w.id}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div className="writeup-number" aria-hidden>{idx + 1}</div>
          <div className="project-text">
            <Link to={`/writeups/${encodeURIComponent(w.id)}`}>
              <h2>{w.title}</h2>
            </Link>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: '0.4rem' }}>
              <span style={{
                padding: '2px 10px',
                borderRadius: 12,
                fontSize: '0.78rem',
                background: 'rgba(255,255,255,0.05)',
                color: '#9cb8c8',
                cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.08)',
                textTransform: 'capitalize',
              }}>
                {type === 'ctf' ? 'CTF' : 'Finding'}
              </span>
              {w.difficulty && (
                <span style={{
                  padding: '2px 10px',
                  borderRadius: 12,
                  fontSize: '0.78rem',
                  background: diffBg,
                  color: diffColor,
                  border: '1px solid rgba(255,255,255,0.08)',
                  textTransform: 'capitalize',
                }}>
                  {w.difficulty}
                </span>
              )}
              {(w.tags || []).map((t) => (
                <span
                  key={t}
                  onClick={() => setQuery(t)}
                  style={{
                    padding: '2px 10px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    fontSize: '0.78rem',
                    color: '#9cb8c8',
                    cursor: 'pointer',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  #{t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container" id="writeups">
      <Link to="/">&larr; Back</Link>

      <div className="writeups-header">
        <h1>Writeups</h1>

        <div className="writeups-controls">
          <button
            type="button"
            onClick={() => setSpotlightOpen(true)}
            className={`writeups-search-trigger${query ? ' writeups-search-trigger--filled' : ''}`}
          >
            <svg viewBox="0 0 24 24" aria-hidden focusable="false">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
            <span className="writeups-search-trigger__label">{query || 'Search writeups'}</span>
            <kbd>⏎</kbd>
          </button>

          <div className="writeups-type-toggle">
            <button
              onClick={() => setTypeFilter('finding')}
              className={`writeups-type-btn${typeFilter === 'finding' ? ' writeups-type-btn--active' : ''}`}
            >
              Findings
            </button>
            <button
              onClick={() => setTypeFilter('ctf')}
              className={`writeups-type-btn${typeFilter === 'ctf' ? ' writeups-type-btn--active' : ''}`}
            >
              CTF
            </button>
          </div>
        </div>
      </div>

      <SpotlightSearch
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
        query={query}
        setQuery={setQuery}
        items={writeups}
      />

      {filtered.length === 0 && (
        <p style={{ color: '#aaa', marginTop: '1rem' }}>No writeups match your search.</p>
      )}

      {/* Both Findings and CTF writeups are grouped by the skill each one
          exercises. Numbering follows reading order down the page, so it
          never appears to jump. */}
      {grouped.map(({ cat, items, offset }) => (
        <section className="writeups-group" key={cat.id}>
          <h2 className="writeups-group-title">{cat.label}</h2>
          <div className="projects-grid">
            {items.map((w, i) => renderCard(w, offset + i))}
          </div>
        </section>
      ))}
    </div>
  );
}
